import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle, ArrowLeft, Loader2, RefreshCw, ShieldAlert, CheckCircle, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function PaymentsHealth() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('without_grn'); // 'without_grn' | 'without_qa'

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stores/payments/health`, { timeout: 60000 });
      setHealth(r.data);
    } catch (e) {
      console.error('payments-health load failed', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchHealth(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-emerald-600" />
      </div>
    );
  }

  const totalIssues = health?.totalIssueCount || 0;
  const withoutGRN = health?.paymentsWithoutGRN || { count: 0, payments: [], totalAmount: 0 };
  const withoutQA = health?.paymentsWithoutQA || { count: 0, payments: [], totalAmount: 0 };
  const healthy = health?.healthy || { count: 0, totalAmount: 0 };

  const activeList = view === 'without_grn' ? withoutGRN : withoutQA;

  return (
    <div className="space-y-6" data-testid="payments-health-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/stores/payments')}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  data-testid="back-to-payments">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldAlert size={24} className="text-rose-600" />
              Payment Health
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Every payment is checked against two simple questions: does its GRN exist, and does its QA exist.
            </p>
          </div>
        </div>
        <button onClick={fetchHealth}
                className="px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                data-testid="refresh-health-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary banner */}
      <div className={`rounded-xl p-5 border ${totalIssues > 0 ? 'bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}>
        {totalIssues > 0 ? (
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-rose-600 mt-0.5" />
            <div>
              <h2 className="font-semibold text-rose-900">
                {totalIssues} payment{totalIssues === 1 ? '' : 's'} flagged out of {health?.totalPayments || 0} total
              </h2>
              <p className="text-sm text-rose-700 mt-1">
                Click a card below to see the affected payments.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle size={20} />
            <p className="font-medium">All {health?.totalPayments || 0} payments have a matching GRN and QA. Nothing diverged.</p>
          </div>
        )}
      </div>

      {/* Three cards: without GRN | without QA | healthy */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setView('without_grn')}
          data-testid="card-without-grn"
          className={`p-5 rounded-xl border text-left transition ${
            view === 'without_grn' ? 'bg-rose-100 border-rose-400 ring-2 ring-rose-200' : 'bg-white border-gray-200 hover:bg-rose-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle size={20} className="text-rose-600" />
            <h3 className="text-sm font-semibold text-gray-700">Payments without matching GRN</h3>
          </div>
          <div className={`text-3xl font-bold ${withoutGRN.count > 0 ? 'text-rose-700' : 'text-gray-400'}`}>
            {withoutGRN.count}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {withoutGRN.count > 0 ? `${fmtMoney(withoutGRN.totalAmount)} affected` : 'all linked GRNs found'}
          </div>
        </button>

        <button
          onClick={() => setView('without_qa')}
          data-testid="card-without-qa"
          className={`p-5 rounded-xl border text-left transition ${
            view === 'without_qa' ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-200' : 'bg-white border-gray-200 hover:bg-amber-50'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={20} className="text-amber-600" />
            <h3 className="text-sm font-semibold text-gray-700">Payments without matching QA</h3>
          </div>
          <div className={`text-3xl font-bold ${withoutQA.count > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
            {withoutQA.count}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {withoutQA.count > 0 ? `${fmtMoney(withoutQA.totalAmount)} affected` : 'all linked QAs found'}
          </div>
        </button>

        <div
          data-testid="card-healthy"
          className="p-5 rounded-xl border bg-emerald-50 border-emerald-200"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={20} className="text-emerald-600" />
            <h3 className="text-sm font-semibold text-gray-700">Healthy payments (GRN + QA both exist)</h3>
          </div>
          <div className="text-3xl font-bold text-emerald-700">{healthy.count}</div>
          <div className="text-xs text-gray-500 mt-1">{fmtMoney(healthy.totalAmount)} settled correctly</div>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-sm">
            {view === 'without_grn' ? 'Payments whose GRN no longer exists' : 'Payments whose GRN exists but has no QA'}
            <span className="ml-2 text-xs text-gray-500">({activeList.count} record{activeList.count === 1 ? '' : 's'})</span>
          </h3>
        </div>
        {activeList.payments.length === 0 ? (
          <div className="py-10 text-center text-emerald-700">
            <CheckCircle size={32} className="mx-auto mb-2 opacity-60" />
            No issues in this category — every payment here is healthy.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="health-detail-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Payment #</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">GRN #</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Vendor</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Invoice</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Why flagged</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeList.payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{p.id}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{p.grnNo || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{p.vendor || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{p.invoiceNo || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(p.amount)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{p.status || '—'}</td>
                    <td className="px-3 py-2 text-xs text-rose-700 italic max-w-md">{p.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentsHealth;
